import React, {forwardRef, ReactNode, useImperativeHandle, useMemo, useRef} from 'react';
import {
  Common,
  CoreDoms,
  DEFAULT_ZOOM,
  EditorExperienceType,
  FlowDirection,
  ItemState,
  PreviewAPI,
  PageConstants,
  Viewers,
  ViewMode,
} from '@taopix/taopix-core';
import {PageInterface, ProductLayout} from '@taopix/taopix-core/dist/types/types';
import classNames from 'classnames';
import {DialogStatus} from '../../Enums';
import useViewMode from '../../Hooks/useViewMode';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';

type PreviewProps = {
  page: PageInterface;
  layout: ProductLayout;
  editorType: EditorExperienceType;
  viewMode?: ViewMode;
  gridCellMaxWidth?: number;
  gridCellMaxHeight?: number;
  gridRowGap?: number;
  gridColGap?: number;
  gridCellPadding?: number;
  children?: ReactNode[] | ReactNode;
  className?: string;
  uiLayoutMode?: 'auto' | 'basic'; // Used to determine whether to apply layouts styles to the page container
};

const Preview = forwardRef(
  (
    {
      page,
      layout,
      editorType,
      viewMode = ViewMode.List,
      gridCellMaxWidth,
      gridCellMaxHeight,
      gridRowGap,
      gridColGap,
      gridCellPadding,
      children,
      className,
      uiLayoutMode = 'auto',
    }: PreviewProps,
    fwdRef
  ) => {
    const ui = useUiState();
    const [storeViewMode] = useViewMode();
    const ref = useRef<PreviewAPI<any> | null>(null);
    const pageContainerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLDivElement>(null);

    // if a fowardRef is passed in, yield it
    useImperativeHandle(fwdRef, () => ref.current);

    const id = `${CoreDoms.ViewportName}-${Viewers.Primary}-${page.pagenumber}`;

    const settings = useMemo(() => {
      return {
        viewerName: Viewers.Primary,
        ismobile: false,
        isCanvasListening: false,
        isCalendarEvent: false,
        singlePageThumbnail: false,
        autoLayoutEnabled: false,
        zoom: DEFAULT_ZOOM,
        pageNumber: page.pagenumber,
        cache: false,
        editorType,
        components: {
          background: true,
          bleed: {
            outside: false,
            inside: false,
          },
          gutter: true,
          spine: true,
          flaps: true,
          empty: false,
          wrap: true,
          errors: false,
        },
      };
    }, [viewMode]);

    React.useEffect(() => {
      let api: PreviewAPI<any> = null as any;
      if (!ref.current) {
        api = new PreviewAPI(settings);
        ref.current = api;
      }

      api
        .setSpreadSize({
          pageNumber: page.pagenumber,
          rulersVisible: false,
          rulerOriginHeight: 0,
          borderSize: 0,
          left: 0,
          top: 0,
          gridCellMaxWidth,
          gridCellMaxHeight,
          gridRowGap,
          gridColGap,
          gridCellPadding,
          viewMode: [ViewMode.Preview, ViewMode.Single].includes(viewMode)
            ? ViewMode.List
            : viewMode,
          direction: ViewMode.Single === viewMode ? FlowDirection.Row : FlowDirection.Column,
        })
        .display();

      return () => {
        api.destroy();

        // we may not have a stage if the API is not in the viewport
        if (api.getStage()) {
          api.setStageSize({width: 0, height: 0});
        }
        ref.current = null;
      };
    }, [viewMode, gridCellMaxWidth]);

    const pageContainerClasses = classNames(
      'tpx-pageContainer',
      uiLayoutMode !== 'basic' && [
        'relative',
        'flex',
        'self-center',
        'justify-center',
        'sm:snap-center',
        'transition-transform',
        'shadow-[0_2px_5px_rgb(0_0_0_/_15%)]',
      ],
      // Append the instance's className
      className
    );

    const pageFooterClasses = classNames(
      'flex',
      'flex-col',
      'lg:min-h-xl',
      'content-center',

      // In single page view on small screen, the page name is absolutely positioned to allow the page to center vertically in the space
      storeViewMode === ViewMode.Single && ui.state.isSmallScreen
        ? ['absolute', 'w-full', 'top-full', 'pt-sm']
        : ['mt-sm', 'relative']
    );

    return (
      <>
        <div className={pageContainerClasses} ref={pageContainerRef}>
          <div ref={canvasRef} id={id}></div>
        </div>
        {children && <div className={pageFooterClasses}>{children}</div>}
      </>
    );
  }
);

export default Preview;
